import { getFilterValue, ListResponse, LoadingParams, replaceFilterCondition } from 'framework/list';
import { FilterCondition } from 'framework/types';
import { cloneDeep } from 'lodash';
import { DocumentStorageContextData } from 'framework/documentStorage';
import { FormFieldsData } from 'framework/form';
import useDocumentStorageReceiver from 'domain/shared/documentStorage/useDocumentStorageReceiver';
import { TokenDocumentTypes } from 'framework/documentStorage';
import complianceDocumentValidationSchema from './createupdate/complianceDocumentValidationSchema';
import { ComplianceDocumentFormType, ComplianceDocumentType, ComplianceDocumentTypeFE } from './types';
import { useDateService } from 'framework/utils';
import { useMemo } from 'react';
import { normalizeStoragePath } from 'domain/shared/documentStorage/documentStorageService';
import {
  buildLatestComplianceDocuments,
  mapAssignmentsWithProjects,
  mapComplianceDocumentForCommit,
} from './complianceDocumentHelpers';
import { usePeopleManagementApi } from 'infrastructure/api';
import { useNewProjectService } from 'domain/shared/newProject';

const useComplianceDocumentService = (): DocumentStorageContextData => {
  const { getTimeDifference, getMomentDateObj } = useDateService();
  const { sendPostRequest } = usePeopleManagementApi();
  const { searchBase, searchAccounts } = useNewProjectService();

  const searchDocuments = async (requestParams: LoadingParams): Promise<ListResponse<ComplianceDocumentTypeFE>> => {
    try {
      const params = cloneDeep(requestParams);
      let filter = params.filter || {};
      const searchString = getFilterValue<string>(filter, 'specification:search') || '';

      filter =
        replaceFilterCondition(
          filter,
          'relatedEmployeeAccountIds',
          FilterCondition.IN,
          'relatedEmployeeAccountIds',
          FilterCondition.EQ,
          value => [value],
        ) || filter;
      filter =
        replaceFilterCondition(
          filter,
          'relatedEmployeeProjectIds',
          FilterCondition.IN,
          'relatedEmployeeProjectIds',
          FilterCondition.EQ,
          value => [String(value)],
        ) || filter;
      const allEmployeesFilterValue = getFilterValue<string>(filter, 'relatedEmployeeFilter.is_empty');
      if (allEmployeesFilterValue) {
        const { 'relatedEmployeeFilter.is_empty': _allEmployeesFilter, ...nextFilter } = filter;
        filter = {
          ...nextFilter,
          relatedEmployeeFilter: {
            is_empty: allEmployeesFilterValue === 'YES',
          },
        };
      }

      if (searchString) {
        const { 'specification:search': _, ...nextFilter } = filter;
        filter = {
          ...nextFilter,
          entityId: {
            [FilterCondition.CT]: searchString,
          },
        };
      }

      const request = await sendPostRequest('/policies/search', {
        ...params,
        filter,
      });

      const response: ListResponse<ComplianceDocumentType> = await request.json();

      const documents = await mapAssignmentsWithProjects(response.result || [], searchBase, searchAccounts);
      const finalResult = buildLatestComplianceDocuments(documents, getMomentDateObj, getTimeDifference);

      return {
        ...response,
        result: finalResult,
      };
    } catch {
      return {
        result: [],
        pageNumber: 0,
        totalPages: 0,
      };
    }
  };

  const getDocumentByPaths = useDocumentStorageReceiver<ComplianceDocumentTypeFE>(
    async (paths: string[]): Promise<ComplianceDocumentTypeFE[]> => {
      const request = await sendPostRequest('/policies/files', {
        paths,
      });

      const response: ComplianceDocumentType[] = await request.json();
      return mapAssignmentsWithProjects(response || [], searchBase, searchAccounts);
    },
  );

  const uploadDocument = async (data: FormFieldsData<ComplianceDocumentFormType>) => {
    const request = await sendPostRequest('/policies/commit', mapComplianceDocumentForCommit(data));
    return request.json();
  };

  const editDocument = async (data: FormFieldsData<ComplianceDocumentFormType>) => {
    const request = await sendPostRequest('/policies/commit', mapComplianceDocumentForCommit(data));
    const response = await request.json();
    return {
      ...data,
      ...response,
    };
  };

  const getDocumentToken = async (path: string) => {
    const request = await sendPostRequest('/policies/presigned-link', {
      path: normalizeStoragePath(path),
    });
    const responce = (await request.json()) as TokenDocumentTypes;
    return responce.metadata.preSignedUrl;
  };

  const getValidationSchema = () => Promise.resolve(complianceDocumentValidationSchema);

  return useMemo(
    () => ({
      getDocumentByPaths,
      uploadDocument,
      editDocument,
      searchDocuments,
      getValidationSchema,
      getDocumentToken,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

export default useComplianceDocumentService;
